// UserRecognitionWrapper - handles cookie-based user recognition on app startup
// Non-blocking: renders children immediately, handles redirects in background
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userRecognitionHelpers } from '../../services/userRecognitionService';
import { cookieManager } from '../../utils/cookieManager';
import { toast } from 'react-toastify';

const UserRecognitionWrapper = ({ children }) => {
  const [userStatus, setUserStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const initializeUserRecognition = useCallback(async () => {
    try {
      // Skip recognition on auth, admin, and link pages
      const skipPaths = ['/auth/', '/admin/', '/login', '/link/'];
      const shouldSkip = skipPaths.some(path => location.pathname.startsWith(path));

      if (shouldSkip) return;

      // IMPORTANT: If we're already on the success page, don't redirect again
      if (location.pathname === '/newuser/success') return;

      const recognition = await userRecognitionHelpers.initializeUserRecognition();
      setUserStatus(recognition);

      // Handle different recognition results - but be more selective about redirects
      switch (recognition.action) {
        case 'redirect_dashboard': {
          // IMPORTANT: Check for pending OAuth BEFORE redirecting
          // If user has pending OAuth, HomePage will show the modal - don't interrupt it
          const uuid = cookieManager.getUserUuid();
          if (uuid && location.pathname === '/') {
            try {
              const { default: axios } = await import('axios');
              const { getApiUrl } = await import('../../utils/apiConfig');
              const response = await axios.get(`${getApiUrl()}/users/${uuid}/pending-oauth`);

              if (response.data.hasPendingOAuth) {
                return; // Don't redirect - let PendingOAuthModal show
              }
            } catch (error) {
              // If check fails, proceed with redirect (safe fallback)
            }
          }

          // No pending OAuth - safe to redirect
          // EXCEPTION: Allow "/" (home page) and /newuser/payment for logged-in users
          const isPaymentPage = location.pathname === '/newuser/payment';
          if (location.pathname.startsWith('/newuser') && !isPaymentPage) {
            navigate('/dashboard');
            return;
          }
          break;
        }

        case 'show_payment_status':
          // Only redirect to success page if we're on the root page, not on other pages
          if (location.pathname === '/') {
            toast.info('You have a successful payment pending account setup. Redirecting...');
            navigate('/newuser/success');
            return;
          }
          break;

        case 'show_success_options': {
          const purchaseResultData = localStorage.getItem('purchase_result');

          if (location.pathname === '/' || !location.pathname.startsWith('/newuser/success')) {
            if (purchaseResultData) {
              toast.success('Your order has been processed successfully!');
              navigate('/newuser/success');
              return;
            }
          }
          break;
        }

        case 'show_purchase_flow':
          // No action needed - let normal routing handle this
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('User recognition failed:', error);
      // On error, just proceed with normal flow
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    initializeUserRecognition();
  }, [initializeUserRecognition]);

  // Add debug info in development
  if (import.meta.env.VITE_ENVIRONMENT === 'development' && userStatus) {
    console.log('User Recognition Status:', userStatus);
    console.log('Cookie Info:', cookieManager.getCookieInfo());

    window.clearUserCookie = () => {
      cookieManager.clearUserUuid();
      console.log('User cookie cleared! Refresh the page to reset user recognition.');
      return 'Cookie cleared successfully';
    };
  }

  // Non-blocking: always render children immediately
  // Redirects happen in the background after recognition completes
  return children;
};

export default UserRecognitionWrapper;
